import I18n from 'I18n';
import { List, Map as ImmutableMap } from 'immutable';
import { TimeSeriesLikeVisualTypes } from '../schema/visual-records';
import { DateTruncationTypes } from '../schema/column-records';
import { getDateResolution, getTimeSeriesLikeReportDateColumnId, isTimeSeriesLikeReport } from '../utils/report-utils';
import { dateTruncTypeToMomentUnitMap } from '../utils/date-truncation-utils';
export const GOAL_ID = '__GOAL__';
const allowedTimeSeriesLikeVisualTypes = List([TimeSeriesLikeVisualTypes.LINE, TimeSeriesLikeVisualTypes.HORIZONTAL_BAR, TimeSeriesLikeVisualTypes.VERTICAL_BAR, TimeSeriesLikeVisualTypes.AREA]);
const getStartMoment = (dataset, dateColumnId) => {
  const firstDatetime = dataset.data.minBy(data => I18n.moment(data.get(dateColumnId))).get(dateColumnId);
  return I18n.moment(firstDatetime);
};
const getEndMoment = (dataset, dateResolution, dateColumnId) => {
  const lastDatetime = dataset.data.maxBy(data => I18n.moment(data.get(dateColumnId))).get(dateColumnId);
  return I18n.moment(lastDatetime).endOf(dateResolution);
};
const getDailyGoalValueForDay = (goalLineValue, goalFrequency, targetDay) => {
  switch (goalFrequency) {
    case DateTruncationTypes.DAY:
      return goalLineValue;
    case DateTruncationTypes.WEEK:
      return goalLineValue / 7;
    case DateTruncationTypes.MONTH:
      return goalLineValue / I18n.moment(targetDay).daysInMonth();
    case DateTruncationTypes.YEAR:
      return goalLineValue / 365;
    default:
      return undefined;
  }
};
const generateAllDays = (startMoment, endMoment) => {
  let result = List();
  let walk = startMoment;
  while (walk <= endMoment) {
    result = result.push(I18n.moment(walk).format('YYYY-MM-DD'));
    walk = I18n.moment(walk).add(1, 'days');
  }
  return result;
};
const getDateColumnId = reportDefinition => {
  return getTimeSeriesLikeReportDateColumnId(reportDefinition, allowedTimeSeriesLikeVisualTypes);
};
const round = (n, places) => {
  const x = Math.pow(10, places);
  return Math.round(n * x) / x;
};
const generateGoalsByTimeBuckets = (reportDefinition, resortedDataset) => {
  const goalResolution = reportDefinition.getIn(['visual', 'options', 'goalFrequency']) || undefined;
  const goalLineValue = reportDefinition.getIn(['visual', 'options', 'goalLineValue']) || undefined;
  if (!goalLineValue || !goalResolution) {
    return resortedDataset;
  }
  const dateColumnId = getDateColumnId(reportDefinition);
  if (!dateColumnId) {
    console.error('Unable to get date column id');
    return resortedDataset;
  }
  const dateResolution = getDateResolution(dateColumnId, reportDefinition);
  const momentDateUnit = dateTruncTypeToMomentUnitMap[dateResolution];
  if (!momentDateUnit) {
    console.error(`Unable to map ${dateResolution} to a moment unit!`);
  }
  const startMoment = getStartMoment(resortedDataset, dateColumnId);
  const endMoment = getEndMoment(resortedDataset, momentDateUnit, dateColumnId);
  const allDays = generateAllDays(startMoment, endMoment);
  const goalsByDay = allDays.map(day => ({
    day,
    goal: getDailyGoalValueForDay(goalLineValue, goalResolution, day)
  }));
  return goalsByDay.groupBy(({
    day
  }) => I18n.moment(day).startOf(momentDateUnit).format('YYYY-MM-DD')).map(days => days.map(({
    goal
  }) => goal).reduce((sum, x = 0) => sum + x, 0)).map(summedGoal => round(summedGoal, 2));
};
export function reportSupportsGoals(reportDefinition) {
  return isTimeSeriesLikeReport(reportDefinition, allowedTimeSeriesLikeVisualTypes);
}
export function hasGoalData(reportDefinition) {
  return reportDefinition.hasIn(['visual', 'options', 'goalLineValue']) && reportSupportsGoals(reportDefinition);
}
export const addGoalData = (reportDefinition, __reportMeta, resortedDataset) => {
  const visual = reportDefinition.visual;
  const hasGoalLineValue = visual ? visual.getIn(['options', 'goalLineValue']) != null : false;
  const dateColumnId = getDateColumnId(reportDefinition);
  if (dateColumnId == null || !hasGoalLineValue || !reportSupportsGoals(reportDefinition) || resortedDataset.data.isEmpty()) {
    return resortedDataset;
  }
  const goalsByTimeBuckets = generateGoalsByTimeBuckets(reportDefinition, resortedDataset);
  return resortedDataset.update('data', data => data.map(datapoint => {
    return datapoint.set(GOAL_ID, goalsByTimeBuckets.get(datapoint.get(dateColumnId)));
  }).toList()).update('properties', properties => properties.set(GOAL_ID, ImmutableMap({
    label: I18n.text('reporting-snowflake.goal.label')
  })));
};
export const __TESTABLE__ = {
  getStartMoment,
  getEndMoment,
  getDailyGoalValueForDay,
  generateAllDays,
  getDateColumnId,
  generateGoalsByTimeBuckets
};